import { InvalidInputError } from '../errors/invalidInputError.js';
import { FormLogger } from '../form/formLogger.js';
import { Message, MessageLike } from '../components/message.js';
import { PaginationInfo } from '../admin/admintable/pagination.js';
import { CSRFToken, Token } from './token.js';
import { TokenStorage } from './tokenStorage.js';
import { ApiClientFactory } from './apiclientfactory';

export type ApiResponse<T = void> = {
    data: T extends void ? any[] | null : T | null;
    language: string;
    message: string;
    pagination: PaginationInfo,
    phpErrors: string[];
    statusCode: number;
    status: string;
    notification: MessageLike;
}

export type ExceptionResponse = {
    message: string;
    exception: Exception|Exception[]; // only available if DISPLAY_ERRORS=1
}
export type Exception = {
    type: string;
    code: number;
    message: string;
    file: string;
    line: number;
}

export type WidgetValidation = {
    widget: string;
    errors: Array<Error>;
}

export class Api {
    static abortController: AbortController = new AbortController();
    static tokenPrefix : string = (window.settings?.general?.name ?? '').toLowerCase().replace(/[^a-z0-9 -]/g, '').replace(/\s+/g, '-');
    static tokenStorage: TokenStorage<Token> = new TokenStorage(`${Api.tokenPrefix}_token`);
    static csrfTokenStorage: TokenStorage<CSRFToken> = new TokenStorage(`${Api.tokenPrefix}_csrfToken`);
    static baseUrl: string = window.baseUrl;
    static lang: string = window.lang;
    static log: boolean = false;
    static showMessages: boolean = false;
    static csrf: boolean = true;

    static showMessage(response: ApiResponse<any>|ExceptionResponse): void {
        if (this.showMessages === false) {
            return;
        }

        // if the response is an exception, show the error message
        if ('exception' in response) {
            Message.log('danger', `${response.message}:<br>${Api.getExceptionResponseMessage(response)}`);
            return;
        }

        if (response.notification === null) {
            return;
        }

        Message.log(response.notification.severity, response.notification.message);
    }

    static getExceptionResponseMessage(response: ExceptionResponse): string {
        if (Array.isArray(response.exception)) {
            return response.exception.map(ex => `${ex.type}: ${ex.message}`).join('<br>');
        }
        return response.exception.message;
    }

    static async request_csrf_token(force: boolean = false): Promise<CSRFToken> {
        let csrfToken = this.csrfTokenStorage.getToken();
        if (csrfToken !== null && force === false) {
            return csrfToken;
        }

        try {
            const userApi = ApiClientFactory(window.baseUrl).user
            const apiResponse = await userApi.csrf();
            csrfToken = new CSRFToken(apiResponse.data.name, apiResponse.data.value);
            this.csrfTokenStorage.setToken(csrfToken); // throws error if token is invalid
            return csrfToken;
        } catch (error) {
            throw new Error(`requestCSRFToken failed: ${error.message ?? error}`);
        }
    }

    /**
     * Returns a Promise to get a valid token
     */
    static async request_token(force = false): Promise<Token> {
        let token = this.tokenStorage.getToken();
        if (token !== null && force === false) {
            return token;
        }

        try {
            const userApi = ApiClientFactory(window.baseUrl).user
            const apiResponse = await userApi.token();

            token = new Token(apiResponse.data.expires, apiResponse.data.token);
            this.tokenStorage.setToken(token);
            this.showMessage(apiResponse);
            return token;
        } catch (error) {
            throw new Error(`request_token failed: ${error.message ?? error}`);
        }
    }

    static async add_csrf_header(headers: Headers, method: 'GET'|'POST'|'PUT'|'PATCH'|'DELETE'): Promise<Headers> {
        // if csrf is disabled, return
        if (this.csrf === false) {
            return headers;
        }

        // if method is GET, OPTIONS or HEAD, return
        // these methods do not require csrf tokens, as they are not modifying data
        if (['GET', 'OPTIONS', 'HEAD'].indexOf(method.toUpperCase()) !== -1) {
            return headers;
        }

        const csrfToken = await this.request_csrf_token();

        headers.append('csrf_name', csrfToken.name);
        headers.append('csrf_value', csrfToken.token);

        return headers;
    }

    /**
     * make an api request, supplying fitting headers
     */
    static async api_request<T>(
        method: 'GET'|'POST'|'PUT'|'PATCH'|'DELETE',
        url: string,
        data: Record<string, unknown>,
        forceLang?: string,
    ): Promise<ApiResponse<T>> {
        try {
            let response: Response;
            const token: Token = await this.request_token();

            window.apiTries = (window.apiTries || {});
            const language = (forceLang !== null && typeof forceLang !== 'undefined') ? forceLang : this.lang;

            try {
                response = await fetch(url, {
                    body: ['GET', 'DELETE'].indexOf(method.toUpperCase()) === -1 ? JSON.stringify(data) : null,
                    method,
                    headers: new Headers({
                        Authorization: `Bearer ${token.token}`,
                        Accept: 'application/json',
                        'Content-Type': 'application/json',
                        [method.toUpperCase() !== 'GET' ? 'Content-Language' : 'Accept-Language']: language,
                    }),
                    signal: this.abortController?.signal,
                });

                await FormLogger.logResponse(response, this.log);
            } catch (error) {
                await FormLogger.logError(error, method !== 'GET' && this.log);
                throw error;
            }

            if (response.status === 204) {
                return {
                    statusCode: 204,
                    status: 'No Content',
                    notification: null,
                    data: [],
                } as ApiResponse<T>;
            }

            // check for json-ness
            const contentType = response.headers.get('Content-Type');
            if (contentType && contentType.indexOf('application/json') === -1) {
                const text = await response.text();
                throw new Error(`Response is not JSON: ${text}`);
            }

            const apiResponse: ApiResponse<T>|ExceptionResponse = await response.json();
            // catch response undefined
            if (typeof apiResponse === 'undefined' || apiResponse === null) {
                throw new Error('Oops! An error occurred.');
            }

            this.showMessage(apiResponse);

            // catch exceptions
            if ('exception' in apiResponse) {
                throw new Error(`${response.status.toString()}: ${apiResponse.message}:<br>${Api.getExceptionResponseMessage(apiResponse)}`);
            }

            // catch 5xx errors
            if (response.status.toString().search(/5\d\d/) === 0) {
                throw new Error(`${response.status.toString()}: ${apiResponse.message}`);
            }

            // catch widget validation errors
            if (response.status === 400 && apiResponse.message === 'invalid input') {
                console.log(apiResponse.data, apiResponse.message);
                throw new InvalidInputError(apiResponse.data as any[], apiResponse.message as any);
            }

            // catch permission errors (try a few times)
            // todo: remove?
            if (response.status === 401 && (window.apiTries[url] = (window.apiTries[url] || 0) + 1) < 5) {
                console.log('retrying with fresh token. tries:', window.apiTries[url]);
                // re-try with new token
                this.tokenStorage.purge();
                return this.api_request(method, url, data, forceLang);
            }

            // catch any non-2xx status codes
            if (response.status.toString().search(/2\d\d/) === -1) {
                throw new Error(apiResponse.message);
            }

            // reset url tries counter
            window.apiTries[url] = 0;

            // resolve with ApiResponse
            return apiResponse;
        } catch (error) {
            if (error instanceof InvalidInputError) {
                throw error;
            } else if (error instanceof DOMException) {
                if (error.name === 'AbortError') return {} as ApiResponse<T>;
                throw error;
            } else {
                error.message = `api_request failed: ${error.message}`;
                throw error;
            }
        }
    }

    /**
     * make a GET request with automatically supplied token
     */
    static api_get<T>(url: string, lang: string = null): Promise<ApiResponse<T>> {
        // make request
        return this.api_request<T>('GET', url, null, lang);
    }

    /**
     * make a POST request with automatically supplied token
     */
    static api_post<T>(url: string, data: Record<string, unknown>): Promise<ApiResponse<T>> {
        // make request
        return this.api_request('POST', url, data, null);
    }

    /**
     * make a PUT request with automatically supplied token
     */
    static api_put<T>(url: string, data: Record<string, unknown>, forceLang?: string): Promise<ApiResponse<T>> {
        // make request
        return this.api_request('PUT', url, data, forceLang);
    }

    /**
     * make a PATCH request with automatically supplied token
     */
    static api_patch<T>(url: string, data: Record<string, unknown>, forceLang?: string): Promise<ApiResponse<T>> {
        // make request
        return this.api_request('PATCH', url, data, forceLang);
    }

    /**
     * make a DELETE request with automatically supplied token
     */
    static api_delete<T>(url: string): Promise<ApiResponse<T>> {
        // make request
        return this.api_request('DELETE', url, null, null);
    }

    /**
     * aborts the current request if supported
     */
    static abort_request(): void {
        if (typeof this.abortController.abort !== 'undefined') {
            this.abortController.abort();
            this.abortController = new AbortController();
        }
    }
}
